import LookupApiService from "@api-services/LookupApiService";

import { GamingLimitCategoryApiService } from "@api-services/account-settings/GamingLimitCategoryService";
import { GamingLimitsApiService } from "@api-services/account-settings/GamingLimitService";
import {
	ResolveLimit,
	ResolveLimitRequest,
	LimitCategoriesViewModel,
	ResolveDepositAndLossLimit,
} from "@data-types";
import { GamingLimitIncreaseConsentLog } from "@api-types/account-settings/GamingLimitIncreaseConsentLog";
import { AccountTypes, SGUID_EMPTY_CONST } from "@utils";
import { filterAndMapLimitCategoriesByTheme } from "@utils/limits/limitsMapper";
import { sortDurations } from "@utils/account-settings/my-limits/sortDurations";
import {
	LimitCategories,
	LimitCategory,
} from "@api-types/myLimits/AvailableCategoriesTwde";
import { LIMITS_CONSTANTS } from "@services/account-settings/MyLimitsConstants";
import { createApiRequestOptions } from "@utils/api-config/createApiRequestOptions";
import { mapAccountTypeToRequestContext } from "@utils/api-config/mapAccountTypeToRequestContext";
import {
	ResolveLimit as ResolveLimitApi,
	ResolveLimitRequest as ResolveLimitRequestApi,
} from "@api-types";
const limitTypesList = ["stake-limit", "loss-limit"];

const ERROR_GAMINGLIMIT = "ERROR_GAMINGLIMIT";

type MappedLimitCategories = Omit<LimitCategories, "selectedCategory"> & {
	selectedCategory: LimitCategory | null;
};

export class MyLimitsService {
	static limitCategories: { [key: string]: LimitCategory } = {};
	static lastResponse: MappedLimitCategories | null = null;

	static async fetchLimitLookups() {
		try {
			const limitDurationsLookup =
				await LookupApiService.fetchGamingLimitDurationsLookup();
			const limitTypesLookup =
				await LookupApiService.fetchGamingLimitTypes();
			const limitTypes: LimitTypeItem[] = [];
			limitTypesLookup?.item.forEach((e: LimitTypeItem) => {
				if (limitTypesList.includes(e.abrv)) {
					limitTypes.push(e);
				}
			});

			return {
				limitDurations: sortDurations(limitDurationsLookup.item),
				limitTypes: limitTypes,
			};
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	static async fetchAvailableCategories(activeAccount: AccountTypes) {
		try {
			const limit =
				await GamingLimitCategoryApiService.getAvailableCategoriesTWDESpecific(
					createApiRequestOptions(
						mapAccountTypeToRequestContext(activeAccount)
					)
				);
			this.lastResponse = limit;

			this.limitCategories = limit.limitCategories.reduce(
				(acc: { [key: string]: LimitCategory }, curr) => {
					acc[curr.gamingLimitCategory.abrv] = curr;
					return acc;
				},
				{}
			);
			return limit;
		} catch (e) {
			throw e;
		}
	}

	static async getTabs() {
		try {
			//multi wallet - fetch categories for online user
			//otherwise fetch categories for user that is logged in
			const user = App.state.rootStore.userAuthStore.user;
			if (!App.state.rootStore.userAuthStore.isMultiUser) {
				if (user?.isInternetUser === true) {
					const primaryGamingLimitResult =
						await this.fetchAvailableCategories(
							AccountTypes.ONLINE
						);

					const primary = this.mapToCategories({
						accountType: AccountTypes.ONLINE,
						result: primaryGamingLimitResult,
					});

					return primary;
				} else {
					const secondaryGamingLimitResult =
						await this.fetchAvailableCategories(
							AccountTypes.OFFLINE
						);

					const secondary = this.mapToCategories({
						accountType: AccountTypes.OFFLINE,
						result: secondaryGamingLimitResult,
					});

					return secondary;
				}
			} else {
				const onlineUserGamingLimitResult =
					await this.fetchAvailableCategories(AccountTypes.ONLINE);

				const tabResult = this.mapToCategories({
					accountType: AccountTypes.ONLINE,
					result: onlineUserGamingLimitResult,
				});

				return {
					tabs: tabResult.tabs,
					isMultiModeEnabled: tabResult.isMultiModeEnabled,
				};
			}
		} catch (error) {
			console.error(error);
			throw error;
		}
	}

	static async getTabData(_: AccountTypes, categoryAbrv: string) {
		if (!this.limitCategories[categoryAbrv]) {
			throw ERROR_GAMINGLIMIT;
		}
		const limit = this.limitCategories[categoryAbrv];
		const limitCategories = this.lastResponse;
		if (!limitCategories) {
			throw ERROR_GAMINGLIMIT;
		}
		limitCategories.selectedCategory = limit;
		return this.mapToViewModel(limitCategories);
	}

	static async resolveLimit(
		activeAccount: AccountTypes,
		model: ResolveLimit
	) {
		const resolveUserLimit: ResolveLimitApi = {
			amount: model.amount,
			gamingLimitAction: model.action,
			gamingLimitCategoryAbrv: model.categoryAbrv,
			gamingLimitDurationAbrv: model.durationAbrv,
			gamingLimitTypeAbrv: model.gamingLimitTypeAbrv,
			limitId: model.limitId === SGUID_EMPTY_CONST ? null : model.limitId,
		};

		try {
			const result = await GamingLimitsApiService.resolveGamingLimit(
				createApiRequestOptions(
					mapAccountTypeToRequestContext(activeAccount)
				),
				resolveUserLimit
			);
			if (result.isSuccessful) {
				const gamingLimitResult: MappedLimitCategories =
					await this.fetchAvailableCategories(activeAccount);
				const limit = this.limitCategories[model.categoryAbrv];
				gamingLimitResult.selectedCategory = limit;
				return this.mapToViewModel(
					gamingLimitResult,
					result?.infoMessage
				);
			}
		} catch (error) {
			console.error(error);
			if (error?.data?.infoMessage) {
				throw {
					message: error.data.infoMessage,
					errorCode: error.data.validationError,
				};
			}
			throw ERROR_GAMINGLIMIT;
		}
	}

	static async resolveDepositLimitWithLoss(
		activeAccount: AccountTypes,
		model: ResolveDepositAndLossLimit
	) {
		try {
			const result =
				await GamingLimitsApiService.resolveDepositLimitWithLoss(
					createApiRequestOptions(
						mapAccountTypeToRequestContext(activeAccount)
					),
					model
				);
			if (result.isSuccessful) {
				const gamingLimitResult: MappedLimitCategories =
					await this.fetchAvailableCategories(activeAccount);
				const limit =
					this.limitCategories[model.activeGamingLimitCategoryAbrv];
				gamingLimitResult.selectedCategory = limit;
				return this.mapToViewModel(
					gamingLimitResult,
					result?.infoMessage
				);
			}
		} catch (error) {
			console.error(error);
			if (error?.data?.infoMessage) {
				throw {
					message: error.data.infoMessage,
					errorCode: error.data.validationError,
				};
			}
			throw ERROR_GAMINGLIMIT;
		}
	}

	static async fetchLastConsent(
		activeAccount: AccountTypes,
		limitId: string
	): Promise<GamingLimitIncreaseConsentLog[]> {
		const result = await GamingLimitsApiService.getLastConsent(
			createApiRequestOptions(
				mapAccountTypeToRequestContext(activeAccount)
			),
			limitId
		);
		return result?.item ?? [];
	}

	static async withdrawConsent(
		activeAccount: AccountTypes,
		gamingLimitId: string
	): Promise<GamingLimitIncreaseConsentLog[]> {
		try {
			const result = await GamingLimitsApiService.withdrawConsent(
				createApiRequestOptions(
					mapAccountTypeToRequestContext(activeAccount)
				),
				gamingLimitId
			);
			await this.fetchAvailableCategories(activeAccount);
			return result?.item ?? [];
		} catch (error) {
			console.error(error);
			if (error?.data?.infoMessage) {
				throw {
					message: error.data.infoMessage,
					errorCode: error.data.validationError,
				};
			}
			throw ERROR_GAMINGLIMIT;
		}
	}

	static async resolveRequest(
		activeAccount: AccountTypes,
		model: ResolveLimitRequest
	) {
		const resolveUserRequestModel: ResolveLimitRequestApi = {
			action: model.action,
			gamingLimitCategoryAbrv: model.categoryAbrv,
			userLimitId: model.userLimitId,
			userLimitRequestId: model.userLimitRequestId,
		};

		try {
			const result =
				await GamingLimitsApiService.resolveGamingLimitRequest(
					createApiRequestOptions(
						mapAccountTypeToRequestContext(activeAccount)
					),
					resolveUserRequestModel
				);
			if (result.isSuccessful) {
				const gamingLimitResult: MappedLimitCategories =
					await this.fetchAvailableCategories(activeAccount);
				const limit = this.limitCategories[model.categoryAbrv];
				gamingLimitResult.selectedCategory = limit;
				return this.mapToViewModel(
					gamingLimitResult,
					result?.infoMessage
				);
			}
		} catch (error) {
			console.error(error);
			if (error?.data?.infoMessage) {
				throw { message: error.data.infoMessage };
			}
			throw ERROR_GAMINGLIMIT;
		}
	}

	static mapToCategories({
		accountType,
		result,
	}: {
		accountType: AccountTypes;
		result: MappedLimitCategories;
	}) {
		const mappedTabs = filterAndMapLimitCategoriesByTheme(
			result.limitCategories
		).map((category) => ({
			id: category.gamingLimitCategoryId,
			name: category.gamingLimitCategory.name,
			abrv: category.gamingLimitCategory.abrv,
			accountType: accountType,
			isMultiModeEnabled: category.isMultiModeEnabled,
		}));
		return {
			tabs: mappedTabs,
			isMultiModeEnabled: result.selectedCategory?.isMultiModeEnabled,
		};
	}

	static mapToViewModel(
		model: MappedLimitCategories,
		infoMessage?: string | null
	): LimitCategoriesViewModel {
		if (!model.selectedCategory) {
			throw ERROR_GAMINGLIMIT;
		}

		const categoryMappings: Record<string, string> = {
			"sports-betting": "deposit-limit",
			deposit: "loss-limit-de",
			"online-casino": "games-deposit-limit",
			"games-deposit": "loss-limit",
		};

		const linkedCategory =
			categoryMappings[model.selectedCategory.gamingLimitCategory.abrv] ||
			null;

		let mappedModel = {
			categoryId: model.selectedCategory.gamingLimitCategoryId,
			categoryName: model.selectedCategory.gamingLimitCategory.name,
			maxAmount: model.selectedCategory.maxAmount,
			minAmount: model.selectedCategory.minAmount,
			isMultiLimitModeEnabled: model.selectedCategory.isMultiModeEnabled,
			isMultiTypeMode: model.selectedCategory.isMultiTypeMode,
			description: this.mapDescriptionToViewModel(
				model.selectedCategory.gamingLimitCategory.description,
				model.selectedCategory.gamingLimitCategory.abrv,
				model.selectedCategory.isMultiModeEnabled,
				model.selectedCategory.isAutoConfirmationEnabled
			),

			activeCategoryAbrv: model.selectedCategory.gamingLimitCategory.abrv,
			durations: model.userLimits
				.filter((ul) => {
					const isSamCategory =
						ul.gamingLimitCategoryId ==
						model.selectedCategory?.gamingLimitCategoryId;
					if (isSamCategory) {
						return true;
					}
					return false;
				})
				.map((ul) => ({
					limitId: ul.id,
					name: ul.gamingLimitDuration?.name,
					canBeUpdated: ul.id !== SGUID_EMPTY_CONST,
					canBeDeleted:
						ul.isDeactivationAllowed && !ul.isDeactivationPending, // false
					categoryId: ul.gamingLimitCategoryId,
					durationId: ul.gamingLimitDurationId,
					typeId: ul.gamingLimitTypeId,
					units: ul.gamingLimitDuration?.units,
					amount: ul.id == SGUID_EMPTY_CONST ? null : ul.amount,
					categoryAbrv: ul.gamingLimitCategory.abrv,
					gamingLimitTypeAbrv: ul.gamingLimitType.abrv,
					gamingLimitTypeName: ul.gamingLimitCategory.name,
					maxValue: this.getMaxLimitPerCategory(
						ul.gamingLimitCategory.abrv
					),
					abrv: ul.gamingLimitDuration.abrv,
					cooldownDate: ul.gamingLimitCooldown,
				})),
			updateInterval: model.selectedCategory.updateInterval,
			requests: model.limitsUpdateRequests
				.filter((lr) => {
					const isSameCategory =
						lr.gamingLimit.gamingLimitCategoryId ==
						model.selectedCategory?.gamingLimitCategoryId;
					if (isSameCategory) {
						return true;
					}

					return (
						linkedCategory &&
						lr.gamingLimit.gamingLimitDuration.units ==
							LIMITS_CONSTANTS.MONTHLY_DURATION_UNITS &&
						lr.gamingLimit.gamingLimitType.abrv == linkedCategory
					);
				})
				.map((lr) => ({
					requestType: RequestTypeEnum[lr.requestType],
					limitType: model.selectedCategory?.isMultiModeEnabled
						? lr.gamingLimit.gamingLimitDuration.name
						: lr.newGamingLimitCombination?.gamingLimitType.name,
					requestedAmount: lr.amount,
					currentAmount: lr.gamingLimit.amount,
					categoryId: lr.gamingLimit.gamingLimitCategoryId,
					requestDate: lr.requestDate,
					expirationDate: lr.expirationDate,
					limitId: model.selectedCategory?.isMultiModeEnabled
						? lr.gamingLimitId
						: lr?.newGamingLimitCombinationId || lr.gamingLimit.id,
					requestId: lr.id,
					limitInterval:
						lr?.newGamingLimitCombination?.gamingLimitDuration
							?.name,
					hasLimitAmountChanged: lr.amount !== lr.gamingLimit.amount,
					hasLimitTypeChanged:
						lr.gamingLimit.gamingLimitTypeId !==
						lr?.newGamingLimitCombination?.gamingLimitTypeId,
					hasLimitDurationChanged:
						lr.gamingLimit.gamingLimitDurationId !==
						lr?.newGamingLimitCombination?.gamingLimitDurationId,
					isDeactivation:
						lr.requestType ===
						RequestTypeEnum["COMMON.LIMITS.LIMIT_DEACTIVATE"],
					gamingLimitCategory:
						lr.gamingLimit.gamingLimitCategory.abrv,
					name: lr.gamingLimit.gamingLimitType.name,
					gamingLimitTypeAbrv: lr.gamingLimit.gamingLimitType.abrv,
					categoryAbrv: lr.gamingLimit.gamingLimitCategory.abrv,
				})),
			infoMessage: infoMessage,
			currency: model.currencyCode,
		};
		if (
			!mappedModel.isMultiLimitModeEnabled &&
			mappedModel.durations != null &&
			mappedModel.durations.length > 0 &&
			model.userLimits != null &&
			model.userLimits.length > 0
		) {
			mappedModel.durations[0].canBeDeleted =
				model.selectedCategory?.isDeactivationAllowed &&
				!model.userLimits[0].isDeactivationPending;
		}
		return mappedModel;
	}

	static mapDescriptionToViewModel(
		title: string,
		tabAbrv: string,
		isMultiMode: boolean,
		isAutoConfirmationEnabled: boolean
	): string[] {
		const descriptionArray = [title];
		if (isMultiMode) {
			//DE only for now
			switch (tabAbrv) {
				case "deposit":
					descriptionArray.push(
						"DESC_FIRST_DEPOSIT",
						"DESC_ALL_LIMIT_PERIODS",
						"DESC_THIRD_NO_DEACTIVATE",
						"DESC_FOURTH_NO_DEACTIVATE",
						"LOSS_VS_DEPOSIT_LIMIT_DESC"
					);
					if (isAutoConfirmationEnabled) {
						descriptionArray.push(
							"DESC_FIFTH_AUTO_CONFIRMATION_NO_DEACTIVATE"
						);
					} else {
						descriptionArray.push("DESC_FIFTH_NO_DEACTIVATE");
					}
					break;
				case "games-deposit":
					descriptionArray.push(
						"DESC_FIRST_DEPOSIT",
						"DESC_ALL_LIMIT_PERIODS",
						"DESC_THIRD_NO_DEACTIVATE",
						"DESC_FOURTH_NO_DEACTIVATE"
					);
					if (isAutoConfirmationEnabled) {
						descriptionArray.push(
							"DESC_FIFTH_AUTO_CONFIRMATION_NO_DEACTIVATE"
						);
					} else {
						descriptionArray.push("DESC_FIFTH_NO_DEACTIVATE");
					}
					break;
				case "sports-betting":
					descriptionArray.push(
						"DESC_FIRST_FIRST",
						"DESC_FIRST_SECOND",
						"DESC_FIRST_THIRD",
						"DESC_ALL_LIMIT_PERIODS",
						"DESC_THIRD_NO_DEACTIVATE",
						"DESC_FOURTH_NO_DEACTIVATE",
						"LOSS_VS_DEPOSIT_LIMIT_DESC"
					);
					if (isAutoConfirmationEnabled) {
						descriptionArray.push(
							"DESC_FIFTH_AUTO_CONFIRMATION_NO_DEACTIVATE"
						);
					} else {
						descriptionArray.push("DESC_FIFTH_NO_DEACTIVATE");
					}
					break;
				case "live-casino":
					descriptionArray.push(
						"DESC_FIRST_LIVE_CASINO",
						"DESC_FIRST_THIRD",
						"DESC_SECOND",
						"DESC_THIRD",
						"DESC_FOURTH"
					);
					if (isAutoConfirmationEnabled) {
						descriptionArray.push("DESC_FIFTH_AUTO_CONFIRMATION");
					} else {
						descriptionArray.push("DESC_FIFTH");
					}
					break;
				case "online-casino":
					descriptionArray.push(
						"DESC_FIRST_GAMES_LIMIT",
						"DESC_GAMES_LIMIT_STAKE",
						"DESC_FIRST_THIRD",
						"DESC_ALL_LIMIT_PERIODS",
						"DESC_THIRD_NO_DEACTIVATE",
						"DESC_FOURTH_NO_DEACTIVATE"
					);
					if (isAutoConfirmationEnabled) {
						descriptionArray.push(
							"DESC_FIFTH_AUTO_CONFIRMATION_NO_DEACTIVATE"
						);
					} else {
						descriptionArray.push("DESC_FIFTH_NO_DEACTIVATE");
					}
					break;
			}
		} else {
			//AT AND COM
			switch (tabAbrv) {
				case "deposit":
				case "games-deposit":
				case "casino-deposit":
				case "live-casino-deposit":
					descriptionArray.push("DESC_FIRST_DEPOSIT", "DESC_SECOND");
					break;
				case "login-time":
					descriptionArray.push("DESC_FIRST_LOGIN_TIME");
					break;
				case "sports-betting":
					descriptionArray.push(
						"DESC_FIRST_FIRST",
						"DESC_FIRST_SECOND",
						"DESC_FIRST_THIRD",
						"DESC_SECOND"
					);
					break;
				case "live-casino":
					descriptionArray.push(
						"DESC_FIRST_LIVE_CASINO",
						"DESC_FIRST_SECOND",
						"DESC_FIRST_THIRD",
						"DESC_SECOND"
					);
					break;
				case "online-casino":
					descriptionArray.push(
						"DESC_FIRST_CASINO",
						"DESC_FIRST_SECOND",
						"DESC_FIRST_THIRD",
						"DESC_SECOND"
					);
					break;
			}
			descriptionArray.push("DESC_THIRD", "DESC_FOURTH");
			if (isAutoConfirmationEnabled) {
				descriptionArray.push("DESC_FIFTH_AUTO_CONFIRMATION");
			} else {
				descriptionArray.push("DESC_FIFTH");
			}
		}

		return descriptionArray;
	}

	static getMaxLimitPerCategory(categoryAbrv: string) {
		switch (categoryAbrv) {
			case "online-casino":
			case "games-deposit":
				return LIMITS_CONSTANTS.GAMES_MAX_DEPOSIT;
			default:
				return LIMITS_CONSTANTS.GENERAL_MAX_AMOUNT;
		}
	}
}

export enum RequestTypeEnum {
	"COMMON.LIMITS.LIMIT_INCREASE" = 1,
	"COMMON.LIMITS.LIMIT_CHANGE" = 2,
	"COMMON.LIMITS.LIMIT_DEACTIVATE" = 3,
}

type LimitTypeItem = {
	abrv: string;
	dateCreated: string;
	dateUpdated: string;
	description: string;
	id: string;
	name: string;
};
